import React, { memo, useEffect, useState } from 'react';
import { AnimatePresence, LayoutGroup, motion, useAnimation } from 'framer-motion';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../authentication/AuthenticationContext';
import { Questions, Survey } from '../account/surveys/Survey';
import { useConfig } from '../ConfigContext';
import { ReferralStatus, RegistrationStatus } from '../account/Account.model';
import { Answers } from '../account/surveys/QuestionTypes/QuestionBase';
import { useSnackbar } from '../snackbar/SnackbarContext';
import { post } from '../utils/Request';

import { mymAlphaQuestions } from './Questions';
import mymLogo from '../_assets/mym-logo-white.webp';
import { GameIds } from '../account/my-games/MyGames.model';
import { UserAdsTracking } from './Preregistration.model';

export function PreregistrationSurvey({ gameId, preRegId }: { gameId: GameIds, preRegId: string }): JSX.Element {
  const [status, setStatus] = useState<RegistrationStatus>(RegistrationStatus.UNREGISTERED);
  const [isSurveyCompleted, setIsSurveyCompleted] = useState<boolean>();
  const [hasSurveyStatusError, setHasSurveyStatusError] = useState<boolean>(false);

  const { t } = useTranslation(['translation', 'mym', 'error']);

  const auth = useAuth();
  const config = useConfig();
  const snackbar = useSnackbar();
  const controls = useAnimation();
  const history = useHistory();

  const randomizeMatrices = (matrixQuestions: Questions[]): Questions[] => {
    const newQuestions = matrixQuestions.map((question) => {
      if ('rows' in question) {
        const newRows = [...question.rows].sort(() => Math.random() - 0.5);
        return {
          ...question,
          rows: newRows,
        };
      }
      return question;
    });

    return newQuestions;
  };

  const submitSurvey = async (answers: Answers, tosAccepted: boolean, utmParams?: UserAdsTracking): Promise<void> => {
    try {
      await post(`${config.clientConfig.host}/players/me/games/${gameId}/pre-registrations/${preRegId}/surveys`, {
        data: {
          response: answers,
          isTosAccepted: tosAccepted,
          registeredFromPaidFlow: utmParams || undefined,
        },
      });
      auth.getPlayer();
      if (auth.user?.preRegistrations[gameId]?.[preRegId].referredBy.status === ReferralStatus.SENT) {
        await post(
          `${config.clientConfig.host}/players/me/games/${gameId}/pre-registrations/${preRegId}/accept-referral`,
        );
        setStatus(RegistrationStatus.REFERRED);
      } else {
        const confirmedStatus = auth.user?.preRegistrations[gameId]?.[preRegId]?.status
      || RegistrationStatus.SELECTED_NO_CLAIM;
        setStatus(confirmedStatus);
      }
    } catch (err) {
      snackbar.addErrorMessage('UnknownError');
    }
  };

  const handleSubmitTos = (isConfirmed: boolean): void => {
    auth.getPlayer();
    if (isConfirmed) {
      const confirmedStatus = auth.user?.preRegistrations[gameId]?.[preRegId]?.status
      || RegistrationStatus.SELECTED_NO_CLAIM;
      setStatus(confirmedStatus);
    }
  };

  const getQuestions = (): Questions[] => {
    switch (gameId) {
      case GameIds.MYM:
        return mymAlphaQuestions as Questions[];
      default:
        return [];
    }
  };

  const handleBack = (): void => {
    history.goBack();
  };

  const getSurveyStatus = async (): Promise<void> => {
    const url = `${config.clientConfig.host}/players/me/games/${gameId}/pre-registrations/${preRegId}/cloneSurvey`;
    try {
      const surveyCompletion = await post<{ hasFilledSurvey: boolean }>(url);
      setIsSurveyCompleted(surveyCompletion.hasFilledSurvey);
      setHasSurveyStatusError(false);
    } catch (e) {
      setHasSurveyStatusError(true);
    }
  };

  useEffect(() => {
    controls.start('visible');

    const preRegistration = auth.user.preRegistrations[gameId];
    if (preRegistration && preRegistration[preRegId] && preRegistration[preRegId].status) {
      setStatus(preRegistration[preRegId].status);
    }
  }, []);

  useEffect(() => {
    getSurveyStatus();
  }, []);

  const Logo = memo((): JSX.Element => {
    switch (gameId) {
      case GameIds.MYM:
        return <img className='logo-img' src={mymLogo} alt='Meet Your Make Logo - White' />;
      default:
        return <></>;
    }
  });

  return (
    <div className={`survey-page-container ${gameId}`}>
      <div className='survey-flow-container'>
        {hasSurveyStatusError ? (
          <>
            <div className='survey-card completed'>
              <div>
                {t('error:UnknownError')}
              </div>
              <button
                type='button'
                onClick={handleBack}
                className='btn btn-primary-game btn-block m-4'
                data-cy='survey-back-btn'
              >
                {t('translation:button.back')}
              </button>
            </div>

          </>
        ) : (
          <LayoutGroup>
            <motion.div
              layout
              className={`logo-container ${status !== RegistrationStatus.UNREGISTERED ? 'registered' : ''}`}
            >
              <motion.div layout='position'>
                <Logo />
                { status !== RegistrationStatus.UNREGISTERED && (
                  <motion.div
                    layout
                    initial={{ opacity: 0, y: 80 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      type: 'tween',
                      ease: 'easeInOut',
                      delay: 0.3,
                    }}
                  >
                    <CheckCircle className='icon success' />
                    {status === RegistrationStatus.REFERRED ? (
                      <>
                        <h3 data-cy='registered-title'>
                          {t('translation:preregistration.referral.acceptedTitle')}
                        </h3>
                        <p>
                          {t('translation:preregistration.referral.acceptedDescription')}
                        </p>
                      </>
                    ) : (
                      <>
                        <h3 data-cy='registered-title'>
                          {t(`${gameId}:preRegistration.${preRegId}.completedTitle`)}
                        </h3>
                        <p>
                          {t(`${gameId}:preRegistration.${preRegId}.completedDescription`)}
                        </p>
                      </>
                    ) }

                    <button
                      type='button'
                      className='btn btn-primary-game btn-block'
                      onClick={() => history.push('/')}
                      data-cy='go-my-account'
                    >
                      Go back to my account
                    </button>
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
            <AnimatePresence>
              { status === RegistrationStatus.UNREGISTERED
               && (
                 <Survey
                   title={t(`${gameId}:preRegistration.${preRegId}.title`)}
                   questions={randomizeMatrices(getQuestions())}
                   submitSurvey={submitSurvey}
                   submitTos={handleSubmitTos}
                   gameId={gameId}
                   preRegId={preRegId}
                   onError={(hasError) => setHasSurveyStatusError(hasError)}
                   isCompleted={isSurveyCompleted}
                 />
               )}
            </AnimatePresence>
          </LayoutGroup>
        )}

      </div>
    </div>
  );
}
